import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, Send, Sparkles, AlertCircle, RotateCcw, X, Square, Wrench, Plus, MessageSquare, Trash2, PanelLeftClose, PanelLeftOpen, Save } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useI18n } from '@/hooks/useI18n';
import { formatDateViewerLocal, formatTimeViewerLocal } from '@/utils/format';
import { useAuthStore } from '@/core/stores/useAuthStore';
import { type AiChatRequest, type StreamPendingData, streamAiChat } from '@/infrastructure/api/ai-chat';
import { apiErrorMessage } from '@/infrastructure/i18n';
import { Textarea } from "@/shared/ui";

// ─── Types ───

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  toolName?: string;
}

interface StoredSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
}

interface SessionStore {
  activeId: string | null;
  sessions: StoredSession[];
}

interface AiChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
}

// ─── Constants ───

const MAX_TURNS = 10;
const STORAGE_KEY_PREFIX = 'ai-sessions-';
const MAX_STORED_MESSAGES = 20;
const MAX_SESSIONS = 20;
const TITLE_MAX_LEN = 30;

// ─── Storage helpers ───

function loadStore(storeId: string): SessionStore {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${storeId}`);
    if (!raw) return { activeId: null, sessions: [] };
    const data = JSON.parse(raw) as SessionStore;
    if (Array.isArray(data.sessions)) return data;
  } catch { /* ignore */ }
  return { activeId: null, sessions: [] };
}

function persistStore(storeId: string, store: SessionStore) {
  // Evict oldest sessions beyond limit
  if (store.sessions.length > MAX_SESSIONS) {
    store.sessions.sort((a, b) => b.updatedAt - a.updatedAt);
    store.sessions = store.sessions.slice(0, MAX_SESSIONS);
  }
  localStorage.setItem(`${STORAGE_KEY_PREFIX}${storeId}`, JSON.stringify(store));
}

function deriveTitle(messages: ChatMessage[]): string {
  const firstUser = messages.find(m => m.role === 'user');
  if (!firstUser) return 'New chat';
  const text = firstUser.content.replace(/\n/g, ' ').trim();
  return text.length > TITLE_MAX_LEN ? text.slice(0, TITLE_MAX_LEN) + '...' : text;
}

function trimMessages(messages: ChatMessage[]): ChatMessage[] {
  const cleaned = messages.map(m => ({ ...m, toolName: undefined }));
  return cleaned.length > MAX_STORED_MESSAGES ? cleaned.slice(-MAX_STORED_MESSAGES) : cleaned;
}

// Desktop-only autoFocus. On mobile, programmatic focus pops the soft keyboard
// (covers half the screen + obscures the suggestion chips / running answer),
// matching Slack/WhatsApp/iMessage behavior of never auto-popping. Operator must
// tap to start typing.
function focusInputDesktop(el: HTMLTextAreaElement | null) {
  if (!el) return;
  if (typeof window !== 'undefined' && window.innerWidth < 768) return;
  el.focus();
}

// ─── Stable plugin references ───

const remarkPlugins = [remarkGfm];
const rehypePlugins = [rehypeHighlight];

// ─── Tool display names ───

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  GetCurrentDate: '获取日期',
  ListStores: '查询门店列表',
  GetStoreInfo: '查询门店信息',
  GetWeather: '查询天气',
  GetStoreOverview: '分析经营概览',
  GetAnomalies: '检测异常',
  GetAnomalyLog: '查看异常日志',
  GetDailySummaryList: '查询日报列表',
  GetDailyReportDetail: '查看日报详情',
  ComparePeriods: '对比分析',
  GetEmployeePerformance: '分析员工表现',
  ListRecentOrders: '查询近期订单',
  GetOrderDetail: '查看订单详情',
  ListShifts: '查询班次',
  ListCreditNotes: '查询退款记录',
  GetCreditNoteDetail: '查看退款详情',
  GetOrderTaxBreakdown: '分析税务明细',
  GetInvoiceCancellationDetail: '查看作废详情',
  GetTopRefundedProducts: '分析退款商品',
  ListChainEntries: '查看链条记录',
  ListProducts: '查询商品',
  ListCategories: '查询分类',
  GetProductSalesRanking: '分析商品销量',
  ListInvoices: '查询发票',
  GetInvoiceDetail: '查看发票详情',
  GetComplianceStatusSummary: '查看合规状态',
  GetStoreResources: '查询门店资源',
};

function toolDisplayName(name: string): string {
  return TOOL_DISPLAY_NAMES[name] || name;
}

function formatSessionDate(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) {
    return formatTimeViewerLocal(ts);
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateViewerLocal(ts);
}

// ─── Component ───

export const AiChatModal: React.FC<AiChatModalProps> = ({ isOpen, onClose, storeId }) => {
  const { t } = useI18n();
  const token = useAuthStore(s => s.token);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [usagePercent, setUsagePercent] = useState<number | null>(null);

  // Mutation awaiting operator confirmation (turn suspended). While set, the
  // input is locked and a confirmation card is shown; the operator must
  // Approve/Deny before the chat can continue.
  const [pendingAction, setPendingAction] = useState<StreamPendingData | null>(null);
  const [editedValue, setEditedValue] = useState('');

  // Session management state
  const [sessions, setSessions] = useState<StoredSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  // Default sidebar collapsed on mobile (full-screen modal — w-56 sidebar would
  // eat half the screen for an empty history list on first launch).
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 768;
  });

  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const denyBtnRef = useRef<HTMLButtonElement>(null);
  const initialized = useRef(false);
  const userScrolledUp = useRef(false);

  const scrollToBottom = useCallback(() => {
    if (!userScrolledUp.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Track user scroll position
  useEffect(() => {
    if (!isOpen) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      userScrolledUp.current = scrollHeight - scrollTop - clientHeight > 100;
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Focus Deny by default when a confirmation card appears — the low-cognition
  // audience must not approve a memory write by reflexively hitting Enter.
  useEffect(() => {
    if (pendingAction) {
      setTimeout(() => denyBtnRef.current?.focus(), 50);
    }
  }, [pendingAction]);

  // Cancel stream when modal closes
  useEffect(() => {
    if (!isOpen && abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setStreaming(false);
    }
  }, [isOpen]);

  // Initialize on first open — load sessions from localStorage
  useEffect(() => {
    if (isOpen && !initialized.current) {
      initialized.current = true;
      const store = loadStore(storeId);
      setSessions(store.sessions);

      if (store.activeId) {
        const active = store.sessions.find(s => s.id === store.activeId);
        if (active) {
          setActiveSessionId(active.id);
          setMessages(active.messages);
          setSessionId(active.id);
          return;
        }
      }
      // No active session — start fresh greeting
      startNewChat();
    }
    if (isOpen) {
      setTimeout(() => focusInputDesktop(inputRef.current), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // ─── Session operations ───

  const startNewChat = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStreaming(false);
    setPendingAction(null);
    setMessages([{
      role: 'assistant',
      content: t('ai.greeting'),
      timestamp: Date.now(),
    }]);
    setSessionId(undefined);
    setActiveSessionId(null);
    setError(null);
    focusInputDesktop(inputRef.current);
  }, [t]);

  const saveCurrentSession = useCallback((sid: string, msgs: ChatMessage[]) => {
    setSessions(prev => {
      const existing = prev.find(s => s.id === sid);
      const trimmed = trimMessages(msgs);
      const title = deriveTitle(msgs);
      let updated: StoredSession[];
      if (existing) {
        updated = prev.map(s => s.id === sid ? { ...s, messages: trimmed, title, updatedAt: Date.now() } : s);
      } else {
        updated = [{ id: sid, title, messages: trimmed, createdAt: Date.now(), updatedAt: Date.now() }, ...prev];
      }
      // Sort by most recent
      updated.sort((a, b) => b.updatedAt - a.updatedAt);
      // Persist
      persistStore(storeId, { activeId: sid, sessions: updated });
      return updated;
    });
    setActiveSessionId(sid);
  }, [storeId]);

  const switchToSession = useCallback((session: StoredSession) => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStreaming(false);
    setPendingAction(null);
    setMessages(session.messages);
    setSessionId(session.id);
    setActiveSessionId(session.id);
    setError(null);
    // Persist active selection
    const store = loadStore(storeId);
    persistStore(storeId, { ...store, activeId: session.id });
    // On mobile, close sidebar after selection
    if (window.innerWidth < 768) setSidebarOpen(false);
    focusInputDesktop(inputRef.current);
  }, [storeId]);

  const deleteSession = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessions(prev => {
      const updated = prev.filter(s => s.id !== id);
      const newActiveId = id === activeSessionId ? null : activeSessionId;
      persistStore(storeId, { activeId: newActiveId, sessions: updated });
      if (id === activeSessionId) {
        startNewChat();
      }
      return updated;
    });
  }, [storeId, activeSessionId, startNewChat]);

  // ─── Chat logic ───

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStreaming(false);
  }, []);

  const userTurnCount = messages.filter(m => m.role === 'user').length;
  const turnsExhausted = userTurnCount >= MAX_TURNS;

  // Shared SSE driver for both a new message (start/continue) and a resume
  // (resolve). The continuation always streams into the LAST assistant bubble.
  const runStream = useCallback((request: AiChatRequest) => {
    if (!token) return;
    const controller = streamAiChat(
      token,
      request,
      {
        onDelta(text) {
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
              updated[updated.length - 1] = { ...last, content: last.content + text, toolName: undefined };
            }
            return updated;
          });
        },
        onTool(data) {
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
              updated[updated.length - 1] = { ...last, toolName: data.name };
            }
            return updated;
          });
        },
        onPending(data) {
          // Turn suspended: lock input + show the confirmation card. Any
          // transitional assistant text is already streamed into the last bubble;
          // drop it if empty so only the card shows.
          setSessionId(data.session_id);
          setPendingAction(data);
          const v = data.proposed_input.value;
          setEditedValue(typeof v === 'string' ? v : '');
          setStreaming(false);
          abortRef.current = null;
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.content) {
              return prev.slice(0, -1);
            }
            return prev;
          });
        },
        onDone(data) {
          setSessionId(data.session_id);
          setUsagePercent(data.daily_limit > 0 ? Math.round((data.used_tokens / data.daily_limit) * 100) : 0);
          setStreaming(false);
          setPendingAction(null);
          abortRef.current = null;
          setMessages(prev => {
            const cleaned = prev.map(m => ({ ...m, toolName: undefined }));
            saveCurrentSession(data.session_id, cleaned);
            return cleaned;
          });
          setTimeout(() => focusInputDesktop(inputRef.current), 50);
        },
        onError(data) {
          setError(apiErrorMessage(t, data.code, data.message));
          setStreaming(false);
          setPendingAction(null);
          abortRef.current = null;
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.content) {
              return prev.slice(0, -1);
            }
            return prev;
          });
        },
      },
    );

    abortRef.current = controller;
  }, [token, t, saveCurrentSession]);

  const sendMessage = useCallback((msg: string) => {
    if (!msg || streaming || !token || pendingAction) return;

    setInput('');
    setError(null);
    if (inputRef.current) inputRef.current.style.height = 'auto';

    userScrolledUp.current = false;
    const userMsg: ChatMessage = { role: 'user', content: msg, timestamp: Date.now() };

    let effectiveSessionId = sessionId;
    if (userTurnCount >= MAX_TURNS) {
      effectiveSessionId = undefined;
      setSessionId(undefined);
    }

    const assistantMsg: ChatMessage = { role: 'assistant', content: '', timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setStreaming(true);

    const request: AiChatRequest = effectiveSessionId
      ? {
          kind: 'continue',
          message: msg,
          store_id: storeId,
          session_id: effectiveSessionId,
        }
      : {
          kind: 'start',
          message: msg,
          store_id: storeId,
        };

    runStream(request);
  }, [streaming, token, sessionId, storeId, userTurnCount, pendingAction, runStream]);

  // Operator resolves the pending mutation. The continuation streams into a
  // fresh assistant bubble. No store_id is ever sent — the server executes on
  // the pending action's store (R1#8).
  const resolvePending = useCallback((decision: 'approve' | 'deny') => {
    if (!pendingAction || streaming || !token) return;
    const action = pendingAction;
    setPendingAction(null);
    setError(null);
    userScrolledUp.current = false;
    setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: Date.now() }]);
    setStreaming(true);

    const updatedInput =
      decision === 'approve' && action.tool_name === 'set_store_memory'
        ? { ...action.proposed_input, value: editedValue }
        : undefined;

    runStream({
      kind: 'resolve',
      session_id: action.session_id,
      action_id: action.action_id,
      decision,
      updated_input: updatedInput,
    });
  }, [pendingAction, streaming, token, editedValue, runStream]);

  const handleSend = useCallback(() => {
    sendMessage(input.trim());
  }, [input, sendMessage]);

  const handleSuggestionClick = useCallback((key: string) => {
    sendMessage(t(key));
  }, [t, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center md:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-3xl h-[100dvh] sm:h-[85dvh] md:h-[80vh] md:max-h-[700px] flex overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">

        {/* ─── Sidebar ─── */}
        <div className={`${sidebarOpen ? 'w-56' : 'w-0'} shrink-0 bg-slate-50 border-r border-slate-200 flex flex-col transition-all duration-200 overflow-hidden`}>
          {/* Sidebar header */}
          <div className="flex items-center justify-between px-3 py-3 border-b border-slate-200 shrink-0">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('ai.history')}</span>
            <button
              onClick={startNewChat}
              className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
              title={t('ai.new_chat')}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Session list */}
          <div className="flex-1 overflow-y-auto overscroll-contain py-1">
            {sessions.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <MessageSquare className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-400">{t('ai.no_history')}</p>
              </div>
            ) : (
              sessions.map(session => (
                <div
                  key={session.id}
                  onClick={() => switchToSession(session)}
                  className={`group flex items-center gap-2 px-3 py-2.5 mx-1.5 my-0.5 rounded-lg cursor-pointer transition-colors ${
                    session.id === activeSessionId
                      ? 'bg-violet-100 text-violet-900'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{session.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{formatSessionDate(session.updatedAt)}</p>
                  </div>
                  <button
                    onClick={(e) => deleteSession(session.id, e)}
                    className="p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 text-slate-400 hover:text-red-500 rounded transition-all shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Usage bar at bottom of sidebar */}
          {usagePercent !== null && (
            <div className="px-3 py-2.5 border-t border-slate-200 shrink-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">{t('ai.usage_percent', { percent: usagePercent })}</span>
              </div>
              <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-violet-500'
                  }`}
                  style={{ width: `${Math.min(usagePercent, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ─── Main chat area ─── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-slate-200 shrink-0 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen(prev => !prev)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
              </button>
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-semibold text-slate-900">{t('ai.title')}</h1>
                <p className="text-xs text-slate-400">{t('ai.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 1 && (
                <button onClick={startNewChat} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" title={t('ai.new_chat')}>
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain px-4 md:px-5 py-4 space-y-4">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center shrink-0 mt-1 mr-2">
                    <Bot className="w-3.5 h-3.5 text-violet-500" />
                  </div>
                )}
                <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-primary-500 text-white rounded-br-md'
                    : 'bg-slate-50 border border-slate-200 text-slate-800 rounded-bl-md'
                }`}>
                  {msg.role === 'assistant' ? (
                    <div className="text-sm leading-relaxed ai-markdown">
                      {msg.content ? (
                        <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins}>
                          {msg.content}
                        </ReactMarkdown>
                      ) : null}
                      {msg.toolName && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-violet-500">
                          <Wrench className="w-3 h-3 animate-spin" style={{ animationDuration: '2s' }} />
                          <span>{toolDisplayName(msg.toolName)}</span>
                        </div>
                      )}
                      {streaming && i === messages.length - 1 && !msg.toolName && (
                        <span className="inline-block w-0.5 h-4 bg-violet-500 animate-pulse ml-0.5 align-text-bottom" />
                      )}
                    </div>
                  ) : (
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">
                      {msg.content}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Confirmation card — turn suspended on a gated mutation. */}
            {pendingAction && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center shrink-0 mt-1 mr-2">
                  <Bot className="w-3.5 h-3.5 text-violet-500" />
                </div>
                <div className="max-w-[85%] md:max-w-[70%] rounded-2xl rounded-bl-md border border-violet-200 bg-violet-50 px-4 py-3 space-y-3">
                  <div className="flex items-center gap-2 text-violet-700">
                    <Save className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-semibold">{t('ai.confirm.title')}</span>
                  </div>
                  {pendingAction.tool_name === 'set_store_memory' ? (
                    <>
                      <p className="text-sm text-slate-700 leading-relaxed">{t('ai.confirm.memory_explain')}</p>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {t('ai.confirm.memory_label')}
                        </label>
                        <Textarea
                          value={editedValue}
                          onChange={(e) => setEditedValue(e.target.value)}
                          rows={2}
                          className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                        />
                      </div>
                    </>
                  ) : (
                    // Future tools: best-effort show the backend description.
                    <p className="text-sm text-slate-700 leading-relaxed">{pendingAction.description}</p>
                  )}
                  <div className="flex items-center justify-end gap-2 pt-0.5">
                    <button
                      ref={denyBtnRef}
                      onClick={() => resolvePending('deny')}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-colors"
                    >
                      {t('ai.confirm.deny')}
                    </button>
                    <button
                      onClick={() => resolvePending('approve')}
                      disabled={pendingAction.tool_name === 'set_store_memory' && !editedValue.trim()}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-violet-500/40 transition-colors"
                    >
                      {t('ai.confirm.approve')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="flex justify-center">
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick suggestions */}
          {messages.length === 1 && !streaming && (
            <div className="shrink-0 px-4 md:px-5 pb-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto">
                {(['ai.suggestion_1', 'ai.suggestion_2', 'ai.suggestion_3', 'ai.suggestion_4'] as const).map(key => (
                  <button key={key} onClick={() => handleSuggestionClick(key)}
                    className="text-left px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors line-clamp-2">
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Context limit banner */}
          {turnsExhausted && !streaming && (
            <div className="shrink-0 px-4 md:px-5 pb-2">
              <div className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                <span className="text-xs text-amber-700">{t('ai.context_limit')}</span>
                <button onClick={startNewChat} className="text-xs font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap">
                  {t('ai.new_chat')}
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="shrink-0 border-t border-slate-200 px-4 md:px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  pendingAction
                    ? t('ai.confirm.input_locked')
                    : turnsExhausted
                      ? t('ai.context_limit_short')
                      : t('ai.input_placeholder')
                }
                rows={1}
                className="flex-1 resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-base sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-colors"
                style={{ height: 'auto', minHeight: '44px', maxHeight: '128px' }}
                disabled={streaming || pendingAction !== null}
              />
              {streaming ? (
                <button
                  onClick={cancelStream}
                  className="shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-slate-500 text-white hover:bg-slate-600 transition-colors"
                >
                  <Square className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || pendingAction !== null}
                  className="shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
